import type { SalesOrder, SalesOrderFormData, SalesOrderPaymentStatus } from '../types/salesOrder';
import api from './api';
import { customerService } from './customerService';
import { cache } from '../utils/cache';
import { translate } from '../i18n/translate';
import { readLanguagePreferencesFromStorage } from '../features/language/preferences/storage';
import { normalizeListResponse } from '../utils/listResponse';

const t = (key: string, fallback?: string) =>
  translate(readLanguagePreferencesFromStorage().languageCode, key, undefined, fallback);

export interface ListSalesOrdersParams {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
}

export const salesOrderService = {
  getAll: async (): Promise<SalesOrder[]> => {
    return cache.fetch('sales_orders_all', async () => {
      const response = await api.get<BackendSalesOrder[]>('/sales-orders');
      return (response.data || []).map(transformResponse);
    });
  },

  list: async (params: ListSalesOrdersParams = {}): Promise<{ data: SalesOrder[]; total: number }> => {
    const { page = 1, size = 25, search, status } = params;
    const query = new URLSearchParams();
    query.set('limit', String(size));
    query.set('offset', String((page - 1) * size));
    query.set('include_meta', 'true');
    if (search && search.trim()) query.set('search', search.trim());
    if (status && status !== 'all') query.set('status', status.toLowerCase());
    const response = await api.get(`/sales-orders?${query.toString()}`);
    const normalized = normalizeListResponse<BackendSalesOrder>(response.data);
    return { data: normalized.data.map(transformResponse), total: normalized.total };
  },

  getById: async (id: string, opts?: { fresh?: boolean }): Promise<SalesOrder | undefined> => {
    // The edit form hydrates from here. Pass { fresh: true } to bypass the
    // 5-min TTL so the form never opens on stale data (which could otherwise
    // overwrite a newer server state on save — a lost update under multi-user).
    if (opts?.fresh) cache.delete(`sales_orders_${id}`);
    return cache.fetch(`sales_orders_${id}`, async () => {
      try {
        const response = await api.get<BackendSalesOrder>(`/sales-orders/${id}`);
        return transformResponse(response.data);
      } catch (error) {
        console.error('Error fetching sales order:', error);
        return undefined;
      }
    });
  },

  create: async (data: SalesOrderFormData): Promise<SalesOrder> => {
    // Resolve the buyer name → id. Include onboarding samples so a practice
    // order's seeded sample customer resolves (getAll() filters it out by
    // default). Harmless for real orders: the sample customer only exists during
    // onboarding and its name won't match a real buyer.
    const customers = await customerService.getAll({ includeOnboardingSamples: true });
    let customerId = customers.find(c => c.name.toLowerCase() === data.customer.toLowerCase())?.id;
    const formMessageOnInvoice = (data.messageOnInvoice || '').trim();
    let customerMessageOnInvoice = '';
    
    if (!customerId) {
      const email = (data.customerEmail || '').trim();
      const billingAddress = (data.billingAddress || '').trim();
      if (!email || !billingAddress) {
        throw new Error(t('salesOrder.service.customerNotFoundCreateFirst'));
      }
      const shippingAddress = (data.shippingAddress || '').trim() || billingAddress;
      const newCustomer = await customerService.create({
        name: data.customer,
        email,
        phone: '',
        billingAddress,
        shippingAddress,
        messageOnInvoice: '',
        internalNotes: ''
      });
      customerId = newCustomer.id;
      customerMessageOnInvoice = (newCustomer.messageOnInvoice || '').trim();
    } else {
      const selectedCustomer = customers.find(c => c.id === customerId);
      customerMessageOnInvoice = (selectedCustomer?.messageOnInvoice || '').trim();
    }

    const shippingAmount = data.shippingAmount ?? 0;
    const tags = data.tags
      ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      : [];
    const effectiveMessageOnInvoice = formMessageOnInvoice || customerMessageOnInvoice || undefined;

    const payload = {
      customer_id: customerId,
      customer_email: data.customerEmail || undefined,
      email_cc: data.emailCc || undefined,
      email_bcc: data.emailBcc || undefined,
      send_later: data.sendLater ?? false,
      status: 'draft',
      payment_status: data.paymentStatus || 'unpaid',
      order_date: new Date(data.orderDate).toISOString(),
      invoice_date: data.invoiceDate ? new Date(data.invoiceDate).toISOString() : null,
      due_date: data.dueDate ? new Date(data.dueDate).toISOString() : null,
      expected_ship_date: data.expectedShipDate ? new Date(data.expectedShipDate).toISOString() : null,
      billing_address: data.billingAddress || undefined,
      shipping_address: data.shippingAddress || undefined,
      terms: data.terms || undefined,
      ship_via: data.shipVia || undefined,
      shipping_date: data.shippingDate ? new Date(data.shippingDate).toISOString() : null,
      tracking_number: data.trackingNumber || undefined,
      business_development_executive: data.businessDevelopmentExecutive || undefined,
      tags,
      store: data.store || undefined,
      notes: data.notes || '',
      internal_notes: data.internalNotes || '',
      discount_type: data.discountType || 'amount',
      discount_value: data.discountValue ?? 0,
      shipping_amount: shippingAmount,
      message_on_invoice: effectiveMessageOnInvoice,
      // Onboarding: tag as practice order so backend gates it to step-6 users
      onboarding_sample: data.onboardingSample ?? false,
      items: data.items.map(item => {
        // RB-group lines carry a composite inventoryItemId of the form
        // "${baseGBItemId}:${roastType}" (the synthetic InventoryItem.id used
        // as a single option key in the picker). Split it back into the
        // (item_id, roast_type) pair the backend persists.
        const isRBGroup = !!item.roastType;
        const colonIdx = item.inventoryItemId.indexOf(':');
        const baseItemId = isRBGroup && colonIdx > 0
          ? item.inventoryItemId.slice(0, colonIdx)
          : item.inventoryItemId;
        return {
          item_id: baseItemId,
          roast_type: item.roastType || '',
          // Pin a specific GB lot when set; null means FIFO. Coerced
          // server-side via normalizeLotID, so empty/whitespace also fall back.
          lot_id: item.lotId ? item.lotId : null,
          description: item.description || undefined,
          quantity: item.quantity,
          unit_price: item.unitPrice,
        };
      })
    };

    const response = await api.post<BackendSalesOrder>('/sales-orders', payload);
    cache.invalidate('sales_orders');
    // SO create may decrement stock (GB/RB at draft/sent, FG/RB at delivered),
    // so the next picker render must see fresh inventory + lots data.
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    let created = transformResponse(response.data);

    if (data.status !== 'Draft') {
      const targetStatus = data.status.toLowerCase();
      const statusResponse = await api.patch<BackendSalesOrder>(`/sales-orders/${created.id}/status`, { status: targetStatus });
      created = transformResponse(statusResponse.data);
    }

    return created;
  },

  update: async (id: string, data: SalesOrderFormData): Promise<SalesOrder> => {
    // See create(): include onboarding samples so a practice order's sample
    // customer resolves on status updates (Mark as Sent/Delivered) too.
    const customers = await customerService.getAll({ includeOnboardingSamples: true });
    let customerId = customers.find(c => c.name.toLowerCase() === data.customer.toLowerCase())?.id;
    const formMessageOnInvoice = (data.messageOnInvoice || '').trim();
    let customerMessageOnInvoice = '';
    
    if (!customerId) {
      const email = (data.customerEmail || '').trim();
      const billingAddress = (data.billingAddress || '').trim();
      if (!email || !billingAddress) {
        throw new Error(t('salesOrder.service.customerNotFoundCreateFirst'));
      }
      const shippingAddress = (data.shippingAddress || '').trim() || billingAddress;
      const newCustomer = await customerService.create({
        name: data.customer,
        email,
        phone: '',
        billingAddress,
        shippingAddress,
        messageOnInvoice: '',
        internalNotes: ''
      });
      customerId = newCustomer.id;
      customerMessageOnInvoice = (newCustomer.messageOnInvoice || '').trim();
    } else {
      const selectedCustomer = customers.find(c => c.id === customerId);
      customerMessageOnInvoice = (selectedCustomer?.messageOnInvoice || '').trim();
    }

    const shippingAmount = data.shippingAmount ?? 0;
    const tags = data.tags
      ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      : [];
    const effectiveMessageOnInvoice = formMessageOnInvoice || customerMessageOnInvoice || undefined;

    const payload = {
      customer_id: customerId,
      customer_email: data.customerEmail || undefined,
      email_cc: data.emailCc || undefined,
      email_bcc: data.emailBcc || undefined,
      send_later: data.sendLater ?? false,
      status: data.status.toLowerCase(),
      payment_status: data.paymentStatus || 'unpaid',
      order_date: new Date(data.orderDate).toISOString(),
      invoice_date: data.invoiceDate ? new Date(data.invoiceDate).toISOString() : null,
      due_date: data.dueDate ? new Date(data.dueDate).toISOString() : null,
      expected_ship_date: data.expectedShipDate ? new Date(data.expectedShipDate).toISOString() : null,
      billing_address: data.billingAddress || undefined,
      shipping_address: data.shippingAddress || undefined,
      terms: data.terms || undefined,
      ship_via: data.shipVia || undefined,
      shipping_date: data.shippingDate ? new Date(data.shippingDate).toISOString() : null,
      tracking_number: data.trackingNumber || undefined,
      business_development_executive: data.businessDevelopmentExecutive || undefined,
      tags,
      store: data.store || undefined,
      notes: data.notes || '',
      internal_notes: data.internalNotes || '',
      discount_type: data.discountType || 'amount',
      discount_value: data.discountValue ?? 0,
      shipping_amount: shippingAmount,
      message_on_invoice: effectiveMessageOnInvoice,
      items: data.items.map(item => {
        // RB-group lines carry a composite inventoryItemId of the form
        // "${baseGBItemId}:${roastType}" (the synthetic InventoryItem.id used
        // as a single option key in the picker). Split it back into the
        // (item_id, roast_type) pair the backend persists.
        const isRBGroup = !!item.roastType;
        const colonIdx = item.inventoryItemId.indexOf(':');
        const baseItemId = isRBGroup && colonIdx > 0
          ? item.inventoryItemId.slice(0, colonIdx)
          : item.inventoryItemId;
        return {
          item_id: baseItemId,
          roast_type: item.roastType || '',
          // Pin a specific GB lot when set; null means FIFO. Coerced
          // server-side via normalizeLotID, so empty/whitespace also fall back.
          lot_id: item.lotId ? item.lotId : null,
          description: item.description || undefined,
          quantity: item.quantity,
          unit_price: item.unitPrice,
        };
      })
    };

    const response = await api.put<BackendSalesOrder>(`/sales-orders/${id}`, payload);
    cache.delete(`sales_orders_${id}`);
    cache.delete('sales_orders_all');
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    return transformResponse(response.data);
  },

  updateStatus: async (id: string, status: string): Promise<SalesOrder> => {
    const response = await api.patch<BackendSalesOrder>(`/sales-orders/${id}/status`, { status: status.toLowerCase() });
    cache.delete(`sales_orders_${id}`);
    cache.delete('sales_orders_all');
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    return transformResponse(response.data);
  },

  // Payment is orthogonal to workflow status: this hits a dedicated endpoint and
  // never touches items or stock. Refunds change revenue, so reports are busted.
  updatePayment: async (
    id: string,
    payment: { paymentStatus: SalesOrderPaymentStatus; paymentType?: string; paymentProofUrl?: string | null }
  ): Promise<SalesOrder> => {
    const response = await api.patch<BackendSalesOrder>(`/sales-orders/${id}/payment`, {
      payment_status: payment.paymentStatus,
      payment_type: payment.paymentType || '',
      payment_proof_url: payment.paymentProofUrl ?? null,
    });
    cache.delete(`sales_orders_${id}`);
    cache.delete('sales_orders_all');
    cache.invalidate('reports_');
    return transformResponse(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/sales-orders/${id}`);
    cache.delete(`sales_orders_${id}`);
    cache.delete('sales_orders_all');
    cache.invalidate('inventory');
    cache.invalidate('reports_');
  }
};

interface BackendSalesOrder {
  id: string;
  customer_id?: string;
  customer_name?: string;
  customer_email?: string | null;
  email_cc?: string | null;
  email_bcc?: string | null;
  send_later?: boolean;
  order_number: string;
  status?: string;
  order_date?: string;
  invoice_date?: string | null;
  due_date?: string | null;
  expected_ship_date?: string | null;
  billing_address?: string | null;
  shipping_address?: string | null;
  terms?: string | null;
  ship_via?: string | null;
  shipping_date?: string | null;
  tracking_number?: string | null;
  business_development_executive?: string | null;
  tags?: string[] | null;
  store?: string | null;
  subtotal_amount?: number;
  discount_type?: string | null;
  discount_value?: number;
  discount_amount?: number;
  shipping_amount?: number;
  total_amount: number;
  balance_due?: number;
  payment_status?: string | null;
  payment_type?: string | null;
  payment_proof_url?: string | null;
  notes?: string;
  internal_notes?: string;
  message_on_invoice?: string | null;
  items?: Array<{
    item_id: string;
    roast_type?: string | null;
    lot_id?: string | null;
    lot_number?: string | null;
    process_type?: string | null;
    item_name?: string;
    description?: string | null;
    quantity: number;
    unit_price: number;
    unit_cost?: number;
    total_price?: number;
  }>;
  created_at?: string;
  updated_at?: string;
}

const transformResponse = (item: BackendSalesOrder): SalesOrder => {
  const rawStatus = (item.status || '').toLowerCase();
  const status: SalesOrder['status'] =
    rawStatus === 'draft'
      ? 'Draft'
      : rawStatus === 'sent' || rawStatus === 'confirmed'
      ? 'Sent'
      : rawStatus === 'delivered' || rawStatus === 'shipped' || rawStatus === 'paid' || rawStatus === 'invoiced'
      ? 'Delivered'
      : rawStatus === 'cancelled'
      ? 'Cancelled'
      : 'Draft';

  return {
    id: item.id,
    customer: item.customer_name || '',
    customerEmail: item.customer_email || '',
    emailCc: item.email_cc || '',
    emailBcc: item.email_bcc || '',
    sendLater: item.send_later ?? false,
    orderDate: item.order_date ? item.order_date.split('T')[0] : '',
    invoiceDate: item.invoice_date ? item.invoice_date.split('T')[0] : '',
    dueDate: item.due_date ? item.due_date.split('T')[0] : '',
    expectedShipDate: item.expected_ship_date ? item.expected_ship_date.split('T')[0] : '',
    billingAddress: item.billing_address || '',
    shippingAddress: item.shipping_address || '',
    terms: item.terms || '',
    shipVia: item.ship_via || '',
    shippingDate: item.shipping_date ? item.shipping_date.split('T')[0] : '',
    trackingNumber: item.tracking_number || '',
    businessDevelopmentExecutive: item.business_development_executive || '',
    tags: (item.tags || []).join(', '),
    store: item.store || '',
    status,
    notes: item.notes || '',
    internalNotes: item.internal_notes || '',
    discountType: item.discount_type === 'percent' ? 'percent' : 'amount',
    discountValue: item.discount_value ?? 0,
    shippingAmount: item.shipping_amount ?? 0,
    paymentStatus:
      item.payment_status === 'paid' ? 'paid' : item.payment_status === 'refunded' ? 'refunded' : 'unpaid',
    paymentType: item.payment_type || '',
    paymentProofUrl: item.payment_proof_url ?? null,
    messageOnInvoice: item.message_on_invoice || '',
    items: (item.items || []).map((i) => ({
      // Reconstruct the composite synthetic id for RB-group lines so the form
      // can match it against the synthetic InventoryItem.id; FG/GB stay as the
      // bare inventory_item id.
      inventoryItemId: i.roast_type ? `${i.item_id}:${i.roast_type}` : i.item_id,
      roastType: i.roast_type || '',
      lotId: i.lot_id || '',
      lotNumber: i.lot_number || '',
      processType: i.process_type || '',
      name: i.item_name || t('purchaseOrder.form.values.unknownItem'),
      description: i.description || undefined,
      quantity: i.quantity,
      unitPrice: i.unit_price,
      amount: i.total_price ?? i.quantity * i.unit_price
    })),
    orderNumber: item.order_number,
    totalAmount: item.total_amount,
    createdAt: item.created_at || '',
    updatedAt: item.updated_at || ''
  };
};
